import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Table, InputGroup } from 'react-bootstrap';
import { FaTrash } from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-toastify';
import PaymentMethodSelector from './PaymentMethodSelector';
import MultiplePaymentModal from './MultiplePaymentModal';

const getNextMonthDate = (baseDate, monthsToAdd = 1) => {
  const date = new Date(baseDate);
  date.setMonth(date.getMonth() + monthsToAdd);
  return date.toLocaleDateString('en-GB');
};

// Helper to format date as DD-MM-YYYY
function toDDMMYYYY(date) {
  if (!date) return '';
  if (typeof date === 'string' && date.includes('-')) {
    // Already in YYYY-MM-DD or DD-MM-YYYY
    const parts = date.split('-');
    if (parts[0].length === 4) return `${parts[2]}-${parts[1]}-${parts[0]}`; // YYYY-MM-DD -> DD-MM-YYYY
    if (parts[2].length === 4) return date; // already DD-MM-YYYY
  }
  const d = new Date(date);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}-${month}-${year}`;
}

// Parse 'DD-MM-YYYY' to Date at midnight
function parseDDMMYYYYToDate(dateStr) {
  if (!dateStr || typeof dateStr !== 'string') return null;
  const parts = dateStr.split('-');
  if (parts.length !== 3) return null;
  const [dd, mm, yyyy] = parts;
  const d = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
  if (Number.isNaN(d.getTime())) return null;
  d.setHours(0, 0, 0, 0);
  return d;
}

const Customer2SaleModal = ({ show, onHide, cart = [], customer2, onCreateCustomer2, deliveryPrice = 0 }) => {
  const { token, user } = useAuth();

  const getAuthHeaders = (includeJson = false) => {
    const baseHeaders = {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(user?.ownerId || user?.id ? { 'X-Owner-Id': String(user?.ownerId || user?.id) } : {}),
      ...(user?.staffId ? { 'X-Staff-Id': String(user?.staffId) } : {}),
    };
    return includeJson ? { 'Content-Type': 'application/json', ...baseHeaders } : baseHeaders;
  };
  const [search, setSearch] = useState('');
  const [customer2List, setCustomer2List] = useState([]);
  const [filteredCustomer2s, setFilteredCustomer2s] = useState([]);
  const [selectedCustomer2, setSelectedCustomer2] = useState(customer2 || null);
  const [name, setName] = useState(customer2?.name || '');
  const [phone, setPhone] = useState(customer2?.phone || '');
  const [description, setDescription] = useState(customer2?.description || '');
  const [paymentTerm, setPaymentTerm] = useState(1);
  const [percentage, setPercentage] = useState(0);
  const [payments, setPayments] = useState([]); // {amount, date}
  const [termDates, setTermDates] = useState([]); // array of dates for each term
  const [showTermDateModal, setShowTermDateModal] = useState(false);
  const [editingTermIndex, setEditingTermIndex] = useState(null);
  const [newTermDate, setNewTermDate] = useState('');
  const [showAddPaymentModal, setShowAddPaymentModal] = useState(false);
  const [addingPayment, setAddingPayment] = useState(false);
  const [newPaymentValue, setNewPaymentValue] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [existingCustomerData, setExistingCustomerData] = useState(null);
  const [saleData, setSaleData] = useState(null);
  const [refundsBySellBatch, setRefundsBySellBatch] = useState([]);
  const [fetchedDeliveryPrice, setFetchedDeliveryPrice] = useState(null);
  const [firstPrice, setFirstPrice] = useState(customer2?.FirstPrice || 0);
  const [firstPricePaymentMethod, setFirstPricePaymentMethod] = useState('');
  const [firstPriceHybridPayments, setFirstPriceHybridPayments] = useState([]);
  const [showFirstPriceMultipleModal, setShowFirstPriceMultipleModal] = useState(false);
  const [newPaymentMethod, setNewPaymentMethod] = useState('');
  const [newPaymentHybridPayments, setNewPaymentHybridPayments] = useState([]);
  const [showNewPaymentMultipleModal, setShowNewPaymentMultipleModal] = useState(false);

  // Calculate total cart price (discount removed)
  const totalCart = cart.reduce((sum, item) => sum + (item.qty * item.price), 0);
  // Prefer fetchedDeliveryPrice when available (existing sale); fallback to provided deliveryPrice
  const deliveryForCalc = Number((fetchedDeliveryPrice != null ? fetchedDeliveryPrice : deliveryPrice) || 0);
  // Qoldi = (Jami + Yetkazib berish) - Oldindan to'lov
  const qoldi = (totalCart + deliveryForCalc) - Number(firstPrice || 0);
  // Only apply percentage to Qoldi
  const totalWithPercentage = cart.length > 0 ? (qoldi + (qoldi * (percentage / 100))) : 0;

  // Calculate balance based on existing customer debt or current cart
  const existingDebt = existingCustomerData?.overallDebt || 0;
  const totalPayments = payments.reduce((sum, p) => sum + Number(p.value || p.amount), 0);
  const balance = existingDebt - totalPayments;

  // Check if customer is selected from search (existing customer)
  const isExistingCustomer = selectedCustomer2 && existingCustomerData;

  // Calculate payment terms
  useEffect(() => {
    // Set default term dates if not set
    if (termDates.length !== paymentTerm) {
      const today = new Date();
      const newDates = Array.from({ length: paymentTerm }, (_, i) =>
        getNextMonthDate(today, i + 1)
      );
      setTermDates(newDates);
    }
  }, [paymentTerm, termDates.length]);

  // Ensure termDates are always in 'DD-MM-YYYY' format
  useEffect(() => {
    if (termDates.length !== paymentTerm) {
      const today = new Date();
      const newDates = Array.from({ length: paymentTerm }, (_, i) => {
        const d = new Date(today);
        d.setMonth(d.getMonth() + i + 1);
        return toDDMMYYYY(d);
      });
      setTermDates(newDates);
    }
  }, [paymentTerm, termDates.length]);

  const terms = Array.from({ length: paymentTerm }, (_, i) => {
    const amount = Math.round((totalWithPercentage / paymentTerm) * 100) / 100;
    const dueDate = termDates[i] || toDDMMYYYY(new Date(Date.now() + (i + 1) * 30 * 24 * 60 * 60 * 1000));
    // For demo, mark all as unpaid (red)
    const paid = false;
    return { term: i + 1, amount, dueDate, paid };
  });

  // Build and sort display terms to maintain stable chronological order (earlier dates first)
  const todayMidnight = (() => { const t = new Date(); t.setHours(0, 0, 0, 0); return t; })();
  const displayTermsSource = (existingCustomerData && existingCustomerData.paymentTerms.length > 0 ? existingCustomerData.paymentTerms : terms);
  const displayTerms = [...displayTermsSource].sort((a, b) => {
    // Sort by due date first (earlier dates come first)
    const da = parseDDMMYYYYToDate(a.dueDate);
    const db = parseDDMMYYYYToDate(b.dueDate);

    // Handle cases where one or both dates are invalid
    if (!da && !db) {
      // Both invalid, sort by term number
      return (a.term || 0) - (b.term || 0);
    }
    if (!da) return 1; // a invalid, b valid -> b comes first
    if (!db) return -1; // a valid, b invalid -> a comes first

    // Both valid, sort by date (earlier first)
    if (da.getTime() !== db.getTime()) {
      return da - db;
    }

    // Same date, sort by term number
    return (a.term || 0) - (b.term || 0);
  });

  // Real-time search for customer2
  useEffect(() => {
    if (!search) {
      setFilteredCustomer2s([]);
      return;
    }
    fetch(`/api/customer2/search?q=${encodeURIComponent(search)}`, {
      headers: getAuthHeaders(),
    })
      .then(res => res.json())
      .then(data => {
        setFilteredCustomer2s(Array.isArray(data) ? data : []);
      });
  }, [search, token, user]);

  // Fetch payments for selected customer2
  useEffect(() => {
    if (!selectedCustomer2) {
      setPayments([]);
      setExistingCustomerData(null);
      setSaleData(null);
      return;
    }
    fetch(`/api/customer2-payments?customer2_id=${selectedCustomer2.id}`, {
      headers: getAuthHeaders(),
    })
      .then(async (res) => {
        if (!res.ok) {
          const text = await res.text();
          console.error('Failed to fetch customer2 payments:', text);
          return [];
        }
        return res.json();
      })
      .then(data => {
        setPayments(Array.isArray(data) ? data : []);
      })
      .catch(err => {
        console.error('Error fetching customer2 payments:', err);
        setPayments([]);
      });
  }, [selectedCustomer2, token, user]);

  // Load existing customer data and sale data when customer is selected
  useEffect(() => {
    if (selectedCustomer2?.id) {
      // Parse existing customer data from the selected customer
      try {
        const paymentTerms = selectedCustomer2.paymentTerms ? JSON.parse(selectedCustomer2.paymentTerms) : [];
        const termDates = selectedCustomer2.termDates ? JSON.parse(selectedCustomer2.termDates) : [];

        setExistingCustomerData({
          paymentTerms,
          termDates,
          percent: selectedCustomer2.percent || 0,
          overallDebt: selectedCustomer2.overallDebt || 0
        });

        // Set the form fields with existing data
        setPaymentTerm(paymentTerms.length || 1);
        setPercentage(selectedCustomer2.percent || 0);
        setTermDates(termDates.length > 0 ? termDates : []);
        setFirstPrice(selectedCustomer2.FirstPrice || 0);

        // Fetch sale data if saleId exists
        if (selectedCustomer2.saleId) {
          fetch(`/api/sales/by-sellbatch/${selectedCustomer2.saleId}`, {
            headers: getAuthHeaders(),
          })
            .then(res => res.json())
            .then(data => {
              setSaleData(Array.isArray(data) ? data : []);
            })
            .catch(error => {
              console.error('Error fetching sale data:', error);
              setSaleData(null);
            });
          // Fetch refunds for this SellBatchID and display
          fetch(`/api/refunds?sellBatchId=${encodeURIComponent(selectedCustomer2.saleId)}&limit=500`, {
            headers: getAuthHeaders()
          })
            .then(res => res.json())
            .then(({ items }) => setRefundsBySellBatch(Array.isArray(items) ? items : []))
            .catch(() => setRefundsBySellBatch([]));
        } else {
          setSaleData(null);
          setRefundsBySellBatch([]);
        }
      } catch (error) {
        console.error('Error parsing existing customer data:', error);
        setExistingCustomerData(null);
        setSaleData(null);
      }
    } else {
      setExistingCustomerData(null);
      setSaleData(null);
      setFirstPrice(0);
    }
  }, [selectedCustomer2]);

  // Fetch delivery price for existing sale if flagged as delivery
  useEffect(() => {
    if (isExistingCustomer && saleData && saleData.length > 0) {
      const first = saleData[0] || {};
      const sellBatchID = first.sellbatchid || first.SellBatchID || selectedCustomer2?.saleId;
      const hasDelivery = Boolean(first.isdelivery ?? first.IsDelivery);
      if (hasDelivery && sellBatchID) {
        fetch(`/api/sales/delivery/${sellBatchID}`, {
          headers: getAuthHeaders(),
        })
          .then(res => res.json())
          .then(data => setFetchedDeliveryPrice(Number(data?.delivery_price) || 0))
          .catch(() => setFetchedDeliveryPrice(0));
      } else {
        setFetchedDeliveryPrice(0);
      }
    } else {
      setFetchedDeliveryPrice(null);
    }
  }, [isExistingCustomer, saleData, selectedCustomer2]);

  // Handlers
  const handleSelectCustomer2 = (c) => {
    setSelectedCustomer2(c);
    setName(c.name);
    setPhone(c.phone);
    setDescription(c.description);
    setSearch('');
  };

  const handleClearForm = () => {
    setSelectedCustomer2(null);
    setName('');
    setPhone('');
    setDescription('');
    setPaymentTerm(1);
    setPercentage(0);
    setPayments([]);
    setTermDates([]);
    setExistingCustomerData(null);
    setSaleData(null);
    setSearch('');
    setFirstPrice(0);
  };

  const handleTermBadgeClick = (idx) => {
    setEditingTermIndex(idx);
    setNewTermDate(termDates[idx] || toDDMMYYYY(new Date(Date.now() + (idx + 1) * 30 * 24 * 60 * 60 * 1000)));
    setShowTermDateModal(true);
  };

  const handleTermDateSave = () => {
    const newDates = [...termDates];
    newDates[editingTermIndex] = toDDMMYYYY(new Date(newTermDate.split('-').reverse().join('-')));
    setTermDates(newDates);
    setShowTermDateModal(false);
  };

  const handleAddPayment = () => {
    setShowAddPaymentModal(true);
    setNewPaymentValue('');
    setNewPaymentMethod('');
    setNewPaymentHybridPayments([]);
  };

  const handleAddPaymentConfirm = async () => {
    if (!selectedCustomer2 || !newPaymentValue || addingPayment) return;

    // Validate payment method is selected
    if (!newPaymentMethod) {
      toast.error('To\'lov usulini tanlang');
      return;
    }

    // If hybrid, validate payments are set
    if (newPaymentMethod === 'hybrid' && (!newPaymentHybridPayments || newPaymentHybridPayments.length === 0)) {
      toast.error('Gibrid to\'lovlarni kiriting');
      setShowNewPaymentMultipleModal(true);
      return;
    }

    setAddingPayment(true);
    try {
      const paymentDate = new Date().toISOString().split('T')[0]; // Today's date in YYYY-MM-DD format

      // POST to backend
      const addPaymentRes = await fetch('/api/customer2-payments', {
        method: 'POST',
        headers: getAuthHeaders(true),
        body: JSON.stringify({
          customer2_id: selectedCustomer2.id,
          value: newPaymentValue,
          payment_date: paymentDate,
          ownerId: user?.ownerId || user?.id,
          staffId: user?.staffId,
        })
      });
      if (!addPaymentRes.ok) {
        const errText = await addPaymentRes.text();
        console.error('Failed to add customer2 payment:', errText);
        toast.error('To\'lov qo\'shishda xatolik');
        return;
      }
      // Update local payment terms from response to immediately reflect statuses
      try {
        const created = await addPaymentRes.json();
        if (created?.updatedTerms && selectedCustomer2) {
          setExistingCustomerData(prev => {
            const paymentTerms = created.updatedTerms.map(t => ({
              term: t.term,
              amount: Number(t.amount),
              dueDate: t.dueDate,
              paid: !!t.paid,
            }));
            return prev ? { ...prev, paymentTerms } : { paymentTerms, termDates: [], percent: 0, overallDebt: 0 };
          });
        }
      } catch (e) {
        console.error('Failed parsing add payment response JSON:', e);
      }

      // Refresh payments
      const refreshRes = await fetch(`/api/customer2-payments?customer2_id=${selectedCustomer2.id}`, {
        headers: getAuthHeaders(),
      });
      if (refreshRes.ok) {
        const data = await refreshRes.json();
        setPayments(Array.isArray(data) ? data : []);
      }

      // Record payment transactions
      const paymentMethodMap = {
        'cash': 0,
        'terminal': 1,
        'card': 2,
        'dollars': 3
      };

      if (newPaymentMethod === 'hybrid' && newPaymentHybridPayments.length > 0) {
        // Handle split payments - execute sequentially
        for (const payment of newPaymentHybridPayments) {
          const paymentType = paymentMethodMap[payment.method] || 0;
          try {
            await fetch('/api/payment-transactions', {
              method: 'POST',
              headers: getAuthHeaders(true),
              body: JSON.stringify({
                customer_type: 2, // Customer2
                action: true, // payment
                type: paymentType,
                amount: Number(payment.amount || 0),
                ownerId: user?.ownerId || user?.id,
                staffId: user?.staffId,
              })
            });
          } catch (error) {
            console.error('Error recording payment transaction:', error);
            // Continue with other payments even if one fails
          }
        }
      } else {
        // Single payment
        const paymentType = paymentMethodMap[newPaymentMethod] || 0;
        try {
          await fetch('/api/payment-transactions', {
            method: 'POST',
            headers: getAuthHeaders(true),
            body: JSON.stringify({
              customer_type: 2, // Customer2
              action: true, // payment
              type: paymentType,
              amount: Number(newPaymentValue),
              ownerId: user?.ownerId || user?.id,
              staffId: user?.staffId,
            })
          });
        } catch (error) {
          console.error('Error recording payment transaction:', error);
          // Don't fail the payment if transaction recording fails
        }
      }

      toast.success('To\'lov muvaffaqiyatli qo\'shildi');
      setShowAddPaymentModal(false);
      setNewPaymentMethod('');
      setNewPaymentHybridPayments([]);
    } catch (err) {
      console.error('Error adding payment:', err);
      toast.error('Xatolik yuz berdi');
    } finally {
      setAddingPayment(false);
    }
  };

  const handleSubmit = async () => {
    if (!name.trim() || !phone.trim()) {
      alert('Name and phone are required');
      return;
    }

    if (cart.length === 0) {
      alert('Cart is empty');
      return;
    }

    if (Number(firstPrice) > 0 && !firstPricePaymentMethod) {
      toast.error("Oldindan to'lov uchun to'lov usulini tanlang");
      return;
    }

    if (Number(firstPrice) > 0 && firstPricePaymentMethod === 'hybrid' && (!firstPriceHybridPayments || firstPriceHybridPayments.length === 0)) {
      toast.error("Gibrid to'lovlarni kiriting");
      setShowFirstPriceMultipleModal(true);
      return;
    }

    setSubmitted(true);

    try {
      // Step 1: Create or update customer2
      let customer2Id = selectedCustomer2?.id;
      if (!customer2Id) {
        // Create new customer2
        const customer2Payload = {
          name,
          phone,
          description,
          paymentTerms: terms.map(t => ({ term: t.term, amount: t.amount, dueDate: t.dueDate })),
          percent: percentage,
          saleId: null, // Will be updated after sale creation
          overallDebt: totalWithPercentage,
          termDates,
          firstPrice: Number(firstPrice)
        };
        const customer2Response = await fetch('/api/customer2', {
          method: 'POST',
          headers: getAuthHeaders(true),
          body: JSON.stringify({
            ...customer2Payload,
            ownerId: user?.ownerId || user?.id,
            staffId: user?.staffId,
          })
        });
        if (!customer2Response.ok) {
          const errorText = await customer2Response.text();
          throw new Error(`Failed to create customer2: ${errorText}`);
        }
        const customer2Data = await customer2Response.json();
        customer2Id = customer2Data.id;
      } else {
        // Update existing customer2
        const customer2Payload = {
          name,
          phone,
          description,
          paymentTerms: terms.map(t => ({ term: t.term, amount: t.amount, dueDate: t.dueDate })),
          percent: percentage,
          saleId: null, // Will be updated after sale creation
          overallDebt: totalWithPercentage,
          termDates,
          firstPrice: Number(firstPrice)
        };
        const customer2Response = await fetch(`/api/customer2/${customer2Id}`, {
          method: 'PUT',
          headers: getAuthHeaders(true),
          body: JSON.stringify({
            ...customer2Payload,
            ownerId: user?.ownerId || user?.id,
            staffId: user?.staffId,
          })
        });
        if (!customer2Response.ok) {
          const errorText = await customer2Response.text();
          throw new Error(`Failed to update customer2: ${errorText}`);
        }
      }

      // Step 2: Create sales record for all items in one request
      const items = cart.map(item => ({
        product_id: item.id,
        quantity: item.qty,
        price: item.price,
        batch_id: item.batch_id || null,
        batch_barcode: item.batch_barcode || null
      }));
      const salesPayload = {
        items,
        customer: name,
        customer_id: customer2Id,
        customer_type: 'customer2',
        paymentType: 'credit',
        delivery: false,
        delivery_price: Number(deliveryPrice) || 0
      };
      const salesResponse = await fetch('/api/sales', {
        method: 'POST',
        headers: getAuthHeaders(true),
        body: JSON.stringify({
          ...salesPayload,
          ownerId: user?.ownerId || user?.id,
          staffId: user?.staffId,
        })
      });
      if (!salesResponse.ok) {
        const errorText = await salesResponse.text();
        throw new Error(`Failed to create sales: ${errorText}`);
      }
      const salesData = await salesResponse.json();
      const sellBatchID = salesData.sellBatchID || (Array.isArray(salesData.results) && salesData.results[0]?.SellBatchID);

      // Step 3: Update customer2 with saleId (SellBatchID)
      if (sellBatchID) {
        const finalCustomer2Payload = {
          name,
          phone,
          description,
          paymentTerms: terms.map(t => ({ term: t.term, amount: t.amount, dueDate: t.dueDate })),
          percent: percentage,
          saleId: sellBatchID,
          overallDebt: totalWithPercentage,
          termDates,
          firstPrice: Number(firstPrice)
        };
        const finalCustomer2Response = await fetch(`/api/customer2/${customer2Id}`, {
          method: 'PUT',
          headers: getAuthHeaders(true),
          body: JSON.stringify({
            ...finalCustomer2Payload,
            ownerId: user?.ownerId || user?.id,
            staffId: user?.staffId,
          })
        });
        if (!finalCustomer2Response.ok) {
          const errorText = await finalCustomer2Response.text();
          throw new Error(`Failed to update customer2 with saleId: ${errorText}`);
        }
      }

      // Step 4: Record payment transaction for firstPrice if provided
      if (Number(firstPrice) > 0) {
        // Validate payment method is selected
        if (!firstPricePaymentMethod) {
          throw new Error('Oldindan to\'lov uchun to\'lov usulini tanlang');
        }

        // Map payment method to numeric type: cash=0, terminal=1, card=2, dollars=3
        const paymentMethodMap = {
          'cash': 0,
          'terminal': 1,
          'card': 2,
          'dollars': 3
        };

        if (firstPricePaymentMethod === 'hybrid' && firstPriceHybridPayments.length > 0) {
          // Handle split payments - execute sequentially
          for (const payment of firstPriceHybridPayments) {
            const paymentType = paymentMethodMap[payment.method] || 0;
            try {
              await fetch('/api/payment-transactions', {
                method: 'POST',
                headers: getAuthHeaders(true),
                body: JSON.stringify({
                  customer_type: 2, // Customer2
                  action: true, // payment
                  type: paymentType,
                  amount: Number(payment.amount || 0),
                  ownerId: user?.ownerId || user?.id,
                  staffId: user?.staffId,
                })
              });
            } catch (error) {
              console.error('Error recording payment transaction:', error);
              // Continue with other payments even if one fails
            }
          }
        } else {
          // Single payment
          const paymentType = paymentMethodMap[firstPricePaymentMethod] || 0;
          try {
            await fetch('/api/payment-transactions', {
              method: 'POST',
              headers: getAuthHeaders(true),
              body: JSON.stringify({
                customer_type: 2, // Customer2
                action: true, // payment
                type: paymentType,
                amount: Number(firstPrice),
                ownerId: user?.ownerId || user?.id,
                staffId: user?.staffId,
              })
            });
          } catch (error) {
            console.error('Error recording payment transaction:', error);
            // Don't fail the entire sale if payment transaction recording fails
          }
        }
      }

      alert('Sale saved successfully!');
      onHide();
    } catch (error) {
      alert(`Error saving sale: ${error.message}`);
      setSubmitted(false);
    }
  };

  const overal = totalCart + Number(deliveryPrice);

  return (
    <Modal show={show} onHide={onHide} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>
          Customer2 Sale
          {isExistingCustomer && <span className="badge bg-info ms-2">View Only</span>}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* First Row */}
        <div className="d-flex mb-3 align-items-center justify-content-between" style={{ gap: 8, position: 'relative' }}>
          <InputGroup style={{ maxWidth: 300 }}>
            <Form.Control
              placeholder="search(phone,name)"
              value={search}
              onChange={e => setSearch(e.target.value)}
              disabled={submitted}
            />
          </InputGroup>
          {selectedCustomer2 && (
            <Button
              size="sm"
              variant="outline-secondary"
              onClick={handleClearForm}
              disabled={submitted}
            >
              Clear
            </Button>
          )}
          {/* Dropdown for search */}
          {search && filteredCustomer2s.length > 0 && (
            <div
              className="list-group"
              style={{
                position: 'absolute',
                zIndex: 10,
                left: 0,
                top: '100%',
                width: 300,
                maxHeight: 200,
                overflowY: 'auto',
              }}
            >
              {filteredCustomer2s.map(c => (
                <button
                  type="button"
                  key={c.id}
                  className="list-group-item list-group-item-action"
                  onClick={() => handleSelectCustomer2(c)}
                >
                  <div><b>{c.name}</b></div>
                  <div style={{ fontSize: 13, color: '#888' }}>{c.phone}</div>
                </button>
              ))}
            </div>
          )}
          <Form.Control
            className="ms-2"
            placeholder="Ism/Familya"
            value={name}
            onChange={e => setName(e.target.value)}
            style={{ maxWidth: 200 }}
            readOnly={submitted || isExistingCustomer}
          />
          <Form.Control
            className="ms-2"
            placeholder="Telefon raqami"
            value={phone}
            onChange={e => setPhone(e.target.value)}
            style={{ maxWidth: 200 }}
            readOnly={submitted || isExistingCustomer}
          />
          <Form.Control
            className="ms-2"
            placeholder="Izoh"
            value={description}
            onChange={e => setDescription(e.target.value)}
            style={{ maxWidth: 250 }}
            readOnly={submitted || isExistingCustomer}
          />
        </div>



        {/* Cart/Sale Table */}
        <Table bordered>
          <thead>
            <tr>
              <th>Mahsulot</th>
              <th>Narx</th>
              <th>Miqdor</th>
              <th>Summa</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {isExistingCustomer && saleData && saleData.length > 0 ? (
              <>
                {saleData.map((sale, idx) => (
                  <tr key={`sale-${idx}`}>
                    <td>{sale.product_name || sale.ProductName || '—'}</td>
                    <td>{sale.salepriceperunit ?? sale.SalePricePerUnit ?? 0}</td>
                    <td>{sale.quantitysold ?? sale.QuantitySold ?? 0}</td>
                    <td>{((Number(sale.quantitysold ?? sale.QuantitySold) || 0) * (Number(sale.salepriceperunit ?? sale.SalePricePerUnit) || 0))}</td>
                    <td></td>
                  </tr>
                ))}
                {refundsBySellBatch.length > 0 && (
                  <tr>
                    <th colSpan={5} className="table-danger">Qaytarilgan mahsulotlar</th>
                  </tr>
                )}
                {refundsBySellBatch.map((r, i) => (
                  <tr key={`refund-${i}`} className="table-danger">
                    <td>{r.product_name || '—'}</td>
                    <td>{Number(r.refund_price) || 0}</td>
                    <td>{Number(r.refund_quantity) || 0}</td>
                    <td>{(Number(r.refund_price) || 0) * (Number(r.refund_quantity) || 0)}</td>
                    <td>{r.doer_name || 'EGA'}</td>
                  </tr>
                ))}
              </>
            ) : cart.length === 0 ? (
              <tr><td colSpan={5} className="text-center">No products</td></tr>
            ) : (
              cart.map((item, idx) => (
                <tr key={idx}>
                  <td>{item.name || 'No Name'}</td>
                  <td>{item.price}</td>
                  <td>{item.qty}</td>
                  <td>{item.qty * item.price}</td>
                  <td>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => {
                        if (typeof onCreateCustomer2 === 'function') {
                          onCreateCustomer2(cart.filter((ci, i) => i !== idx));
                        }
                      }}
                      disabled={isExistingCustomer}
                    >
                      <FaTrash />
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
        <div className="text-end mb-2">
          {isExistingCustomer && saleData && saleData.length > 0 ? (
            (() => {
              const saleTotal = saleData.reduce((sum, sale) => {
                const qty = Number(sale.quantitysold ?? sale.QuantitySold) || 0;
                const price = Number(sale.salepriceperunit ?? sale.SalePricePerUnit) || 0;
                return sum + (qty * price);
              }, 0);
              const delivery = fetchedDeliveryPrice || 0;
              const overal = saleTotal + delivery;
              if (delivery > 0) {
                return (
                  <div>
                    <strong>
                      Jami: {saleTotal.toFixed(2)} so'm + {delivery} so'm (Yetkazib berish) = {overal.toFixed(2)} so'm
                    </strong>
                  </div>
                );
              } else {
                return (
                  <div>
                    <strong>Jami: {saleTotal.toFixed(2)} so'm</strong>
                  </div>
                );
              }
            })()
          ) : (
            <div>
              {deliveryPrice > 0 ? (
                <strong>
                  Jami: {totalCart} so'm + {deliveryPrice} so'm (Yetkazib berish) = {overal} so'm
                </strong>
              ) : (
                <strong>Jami: {totalCart} so'm</strong>
              )}
            </div>
          )}
        </div>
        <div>
          <div >
            <InputGroup>
              <InputGroup.Text>Oldindan to'lov</InputGroup.Text>
              <Form.Control
                type="number"
                min={0}
                value={firstPrice}
                onChange={e => setFirstPrice(e.target.value)}
                disabled={submitted || isExistingCustomer}
              />
            </InputGroup>
            {Number(firstPrice) > 0 && !submitted && !isExistingCustomer && (
              <div className="mt-2">
                <label className="form-label small mb-1">To'lov usuli*</label>
                {firstPricePaymentMethod === 'hybrid' ? (
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <span className="badge bg-primary p-2">Gibrid to'lov tanlangan</span>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => setShowFirstPriceMultipleModal(true)}
                    >
                      To'lovlarni ko'rish/tahrirlash
                    </Button>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => {
                        setFirstPricePaymentMethod('');
                        setFirstPriceHybridPayments([]);
                      }}
                    >
                      Bekor qilish
                    </Button>
                  </div>
                ) : (
                  <PaymentMethodSelector
                    value={firstPricePaymentMethod}
                    onChange={(e) => {
                      const value = e.target.value;
                      setFirstPricePaymentMethod(value);
                      if (value === 'hybrid') {
                        setShowFirstPriceMultipleModal(true);
                      }
                    }}
                    onSplitClick={() => {
                      setFirstPricePaymentMethod('hybrid');
                      setShowFirstPriceMultipleModal(true);
                    }}
                    showSplit={true}
                  />
                )}
              </div>
            )}
          </div>
          <div style={{ minWidth: 180 }}>
            <b>Qoldi:</b> {qoldi} so'm
          </div>
        </div>
        {/* Second Row: Payment term, Oldindan to'lov, Qoldi, percentage, and debt information */}
        <div className="d-flex mb-3 align-items-center justify-content-between" style={{ gap: 8 }}>
          <InputGroup style={{ maxWidth: 180 }}>
            <InputGroup.Text>Muddatlar</InputGroup.Text>
            <Form.Control
              type="number"
              min={1}
              value={paymentTerm}
              onChange={e => setPaymentTerm(Math.max(1, Number(e.target.value)))}
              disabled={submitted || isExistingCustomer}
            />
          </InputGroup>

          <InputGroup style={{ maxWidth: 180 }}>
            <InputGroup.Text>Foiz (Qarzga)</InputGroup.Text>
            <Form.Control
              type="number"
              min={0}
              value={percentage}
              onChange={e => setPercentage(Number(e.target.value))}
              disabled={submitted || isExistingCustomer}
            />
          </InputGroup>
          <div style={{ minWidth: 180 }}>
            <b>% bilan jami:</b> {existingCustomerData ? existingCustomerData.overallDebt : totalWithPercentage} so'm
          </div>
          <div style={{ minWidth: 180 }}>
            <b>Balans:</b> {balance} so'm
          </div>
        </div>
        {/* Payment Terms Section */}
        <div className="mb-3">
          <h6>To'lov muddatlari:</h6>
          {displayTerms.map((term, idx) => (
            <span
              key={term.term}
              className="px-3 py-2 me-2 mb-2 d-inline-block"
              style={{
                background: (() => {
                  if (term.paid) return '#4caf50';
                  const d = parseDDMMYYYYToDate(term.dueDate);
                  if (d && d <= todayMidnight) return '#f44336'; // overdue unpaid (incl. today)
                  return '#2196f3'; // upcoming unpaid
                })(),
                color: 'white',
                borderRadius: 16,
                fontWeight: 500,
                cursor: (submitted || isExistingCustomer) ? 'not-allowed' : 'pointer',
                opacity: (submitted || isExistingCustomer) ? 0.7 : 1,
              }}
              onClick={() => !submitted && !isExistingCustomer && handleTermBadgeClick((term.term || idx + 1) - 1)}
            >
              <b>{term.term}</b> | {term.amount} so'm - {term.dueDate}
            </span>
          ))}
        </div>
        {/* Customer Payments Section */}
        <div className="mb-2">
          <h5>Mijoz to'lovlari</h5>
          <div className="mb-2">
            <Button size="sm" onClick={handleAddPayment} disabled={!selectedCustomer2}>
              To'lov qo'shish
            </Button>
          </div>
          <Table bordered>
            <tbody>
              {payments.length === 0 ? (
                <tr><td>No payments</td></tr>
              ) : payments.map((p, idx) => (
                <tr key={idx}>
                  <td>{idx + 1}) {p.value} so'm</td>
                  <td>{p.payment_date || p.paymentDate || p.date}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>

        {/* Show delivery price for existing sale */}
        {isExistingCustomer && fetchedDeliveryPrice !== null && (
          <div className="mb-3">
            <strong>Yetkazib berish narxi:</strong> {fetchedDeliveryPrice} so'm
          </div>
        )}

        {/* Term Date Modal */}
        <Modal show={showTermDateModal} onHide={() => setShowTermDateModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Change Term Date</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group>
              <Form.Label>New Date</Form.Label>
              <Form.Control
                type="date"
                value={(() => {
                  // Convert DD-MM-YYYY to YYYY-MM-DD for input type="date"
                  if (!newTermDate) return '';
                  const parts = newTermDate.split('-');
                  if (parts.length === 3) return `${parts[2]}-${parts[1]}-${parts[0]}`;
                  return '';
                })()}
                onChange={e => {
                  // Convert YYYY-MM-DD to DD-MM-YYYY
                  const val = e.target.value;
                  if (val) {
                    const [y, m, d] = val.split('-');
                    setNewTermDate(`${d}-${m}-${y}`);
                  } else {
                    setNewTermDate('');
                  }
                }}
                disabled={submitted}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowTermDateModal(false)}>Cancel</Button>
            <Button variant="primary" onClick={handleTermDateSave}>Saqlash</Button>
          </Modal.Footer>
        </Modal>
        {/* Add Payment Modal */}
        <Modal show={showAddPaymentModal} onHide={() => setShowAddPaymentModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>To'lov qo'shish</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>To'lov miqdori</Form.Label>
              <Form.Control
                type="number"
                min={1}
                value={newPaymentValue}
                onChange={e => setNewPaymentValue(e.target.value)}
                placeholder="Miqdorni kiriting"
              />
            </Form.Group>
            {Number(newPaymentValue) > 0 && (
              <Form.Group>
                <Form.Label className="mb-2">To'lov usuli*</Form.Label>
                {newPaymentMethod === 'hybrid' ? (
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <span className="badge bg-primary p-2">Gibrid to'lov tanlangan</span>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => setShowNewPaymentMultipleModal(true)}
                    >
                      To'lovlarni ko'rish/tahrirlash
                    </Button>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => {
                        setNewPaymentMethod('');
                        setNewPaymentHybridPayments([]);
                      }}
                    >
                      Bekor qilish
                    </Button>
                  </div>
                ) : (
                  <PaymentMethodSelector
                    value={newPaymentMethod}
                    onChange={(e) => {
                      const value = e.target.value;
                      setNewPaymentMethod(value);
                      if (value === 'hybrid') {
                        setShowNewPaymentMultipleModal(true);
                      }
                    }}
                    onSplitClick={() => {
                      setNewPaymentMethod('hybrid');
                      setShowNewPaymentMultipleModal(true);
                    }}
                    showSplit={true}
                  />
                )}
              </Form.Group>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowAddPaymentModal(false)} disabled={addingPayment}>Bekor qilish</Button>
            <Button variant="primary" onClick={handleAddPaymentConfirm} disabled={!newPaymentValue || addingPayment}>
              {addingPayment ? 'Qo\'shilmoqda...' : 'Qo\'shish'}
            </Button>
          </Modal.Footer>
        </Modal>
        <MultiplePaymentModal
          show={showFirstPriceMultipleModal}
          onHide={() => setShowFirstPriceMultipleModal(false)}
          onConfirm={(payments) => {
            setFirstPriceHybridPayments(payments);
            setShowFirstPriceMultipleModal(false);
          }}
          totalAmount={Number(firstPrice) || 0}
          currency="UZS"
        />
        <MultiplePaymentModal
          show={showNewPaymentMultipleModal}
          onHide={() => setShowNewPaymentMultipleModal(false)}
          onConfirm={(payments) => {
            setNewPaymentHybridPayments(payments);
            setShowNewPaymentMultipleModal(false);
          }}
          totalAmount={Number(newPaymentValue) || 0}
          currency="UZS"
        />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Close</Button>
        <Button variant="primary" onClick={handleSubmit} disabled={submitted || isExistingCustomer}>
          {submitted ? 'Saving...' : isExistingCustomer ? 'View Only' : 'Save'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default Customer2SaleModal; 